/**
 * RelatedOrdersTable - Display related orders for a customer
 */

import React, { useCallback } from "react";
import { Table, Tag, Button, Typography } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

const { Text, Link } = Typography;

const RelatedOrdersTable = ({ orders = [] }) => {
  const navigate = useNavigate();

  const viewOrder = useCallback(
    (record) => {
      if (record.id) {
        navigate(`/orders/${record.id}`);
      }
    },
    [navigate]
  );

  const formatCurrency = (amount) => {
    const value = Number(amount) || 0;
    return `R${value.toLocaleString("en-ZA", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const columns = [
    {
      title: "Order ID",
      dataIndex: "id",
      key: "id",
      render: (id, record) => (
        <Link
          onClick={(event) => {
            event.stopPropagation();
            viewOrder(record);
          }}
        >
          <Text code>{id || "N/A"}</Text>
        </Link>
      ),
    },
    {
      title: "From",
      dataIndex: ["pickup", "address"],
      key: "from",
      render: (address) => address || "N/A",
    },
    {
      title: "To",
      dataIndex: ["delivery", "address"],
      key: "to",
      render: (address) => address || "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const statusMap = {
          pending: { color: "gold", text: "Pending" },
          confirmed: { color: "blue", text: "Confirmed" },
          in_progress: { color: "cyan", text: "In Progress" },
          completed: { color: "green", text: "Completed" },
          cancelled: { color: "red", text: "Cancelled" },
        };
        const cfg = statusMap[status] || { color: "default", text: status || "N/A" };
        return <Tag color={cfg.color}>{cfg.text}</Tag>;
      },
    },
    {
      title: "Booking Status",
      dataIndex: ["booking", "status"],
      key: "bookingStatus",
      render: (status) => {
        if (!status) return <Text type="secondary">N/A</Text>;
        const statusMap = {
          pending: { color: "gold", text: "Pending" },
          scheduled: { color: "blue", text: "Scheduled" },
          confirmed: { color: "green", text: "Confirmed" },
          in_progress: { color: "cyan", text: "In Progress" },
          completed: { color: "green", text: "Completed" },
          cancelled: { color: "red", text: "Cancelled" },
        };
        const cfg = statusMap[status] || { color: "default", text: status || "N/A" };
        return <Tag color={cfg.color}>{cfg.text}</Tag>;
      },
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => {
        if (!date) return "N/A";
        try {
          return format(new Date(date), "MMM dd, yyyy");
        } catch {
          return "Invalid date";
        }
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          aria-label="View order"
          onClick={(event) => {
            event.stopPropagation();
            viewOrder(record);
          }}
          style={{ opacity: 0.6 }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.6")}
        />
      ),
    },
  ];

  if (orders.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <Text type="secondary">No orders found for this customer</Text>
      </div>
    );
  }

  return (
    <Table
      columns={columns}
      dataSource={orders}
      rowKey={(record) => record.id}
      pagination={{ pageSize: 10 }}
      size="small"
      scroll={{ x: "max-content" }}
      onRow={(record) => ({
        onClick: () => viewOrder(record),
        style: { cursor: "pointer" },
      })}
    />
  );
};

export default RelatedOrdersTable;
